import { SplitText } from 'gsap/SplitText';

export class MediaFadeContentSlide {
    constructor (context) {
        if (!context) return;
        this.context = context;

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.observer = null;

        this.mediaFadeElement = context.querySelector('.js-animation-media-fade-content-slide-media');
        this.fadeAnimation = null;

        this.slideElements = [...context.querySelectorAll('.js-animation-media-fade-content-slide-content')];
        this.slideAnimations = [];
        this.splitSlideAnimation = null;

        this.isAnimating = false;
        this.hasAnimated = false;

        this.init();
    }

    init () {
        if (this.supportsWAAPI()) {
            this.observer = new IntersectionObserver(entries => {
                entries.forEach(entry => this.handleIntersection(entry));
            });

            if (this.context.querySelector('.js-animation-media-fade-content-slide-content-split')) {
                this.splitSlideAnimation = SplitText.create(this.context.querySelector('.js-animation-media-fade-content-slide-content-split'), {
                    type: 'lines',
                    linesClass: 'split-line js-animation-media-fade-content-slide-content',
                    onSplit: (self) => {
                        self.elements[0].classList.remove('opacity-0');
                        this.slideElements = [...this.context.querySelectorAll('.js-animation-media-fade-content-slide-content')];
                        this.observer.observe(this.context);
                    }
                });
            } else {
                this.observer.observe(this.context);
            }

        } else {
            document.documentElement.classList.add('js-no-waapi');
        }
    }

    supportsWAAPI () {
        return typeof Element.prototype.animate === 'function' && 
               typeof Animation !== 'undefined';
    }

    persistFinalStyles (element, styles = {}) {
        if (!element) return;

        Object.entries(styles).forEach(([property, value]) => {
            element.style[property] = value;
        });

        element.classList.remove('opacity-0');
    }

    async handleIntersection (entry) {
        if (!entry.isIntersecting || this.isAnimating || this.hasAnimated) return;

        this.isAnimating = true;

        try {
            this.fadeAnimation = this.mediaFadeElement.animate([
                { opacity: 0 },
                { opacity: 1 }
            ], {
                duration: 1000,
                easing: 'ease-in-out',
                fill: 'forwards'
            });

            if (this.prefersReducedMotion) this.fadeAnimation.finish();

            this.slideElements.forEach((element, index) => {
                this.slideAnimations[index] = element.animate([
                    { transform: 'translateY(50px)', opacity: 0 },
                    { transform: 'translateY(0%)', opacity: 1 }
                ], {
                    duration: 1000,
                    delay: 50 * (index + 1),
                    easing: 'ease-in-out',
                    fill: 'forwards'
                });

                if (this.prefersReducedMotion) this.slideAnimations[index].finish();
            });

            await this.fadeAnimation.finished;

            this.persistFinalStyles(this.mediaFadeElement, {
                opacity: '1'
            });
            this.fadeAnimation.cancel();

            await Promise.all(
                this.slideAnimations.map(async (animation, index) => {
                    await animation.finished;

                    this.persistFinalStyles(this.slideElements[index], {
                        opacity: '1',
                        transform: 'translateY(0)'
                    });

                    animation.cancel();
                })
            );

            this.splitSlideAnimation?.revert();

            const splitContentElement = this.context.querySelector('.js-animation-media-fade-content-slide-content-split');
            this.persistFinalStyles(splitContentElement, {
                opacity: '1',
                transform: 'translateY(0)'
            });

            this.hasAnimated = true;
            this.observer.unobserve(entry.target);
            this.observer.disconnect();
        } finally {
            this.isAnimating = false;
        }
    }
}

export class MediaScale {
    constructor () {
        this.contexts = [...document.querySelectorAll('.js-animation-media-scale')];

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.observer = null;

        this.previousAnimationElement = null;

        if (this.contexts.length) this.init();
    }

    init () {
        if (this.supportsWAAPI()) {
            this.observer = new IntersectionObserver(entries => {
                entries.forEach(entry => this.handleIntersection(entry));
            });

            this.contexts.forEach(context => {
                this.observer.observe(context);
            });
        } else {
            document.documentElement.classList.add('js-no-waapi');
        }
    }

    supportsWAAPI () {
        return typeof Element.prototype.animate === 'function' && 
               typeof Animation !== 'undefined';
    }

    async handleIntersection (entry) {
        if (!entry.isIntersecting) return;

        // This function will detect if the `entry.target` is positioned on the same row (but not on top of) as the previous element and compound the delay accordingly.
        const delay = () => {
            let delay = 0;
            if (this.previousAnimationElement?.parentElement.getBoundingClientRect().top === entry.target.parentElement.getBoundingClientRect().top &&
                this.previousAnimationElement?.parentElement.getBoundingClientRect().left !== entry.target.parentElement.getBoundingClientRect().left) {
                delay = 200 + this.previousAnimationElement.getAnimations()[0]?.effect.getComputedTiming().delay;
            }

            return delay;
        }

        try {
            const scaleAnimation = entry.target.animate([
                { transform: 'scale(1.1)', opacity: 0 },
                { transform: 'scale(1)', opacity: 1 }
            ], {
                duration: 1000,
                delay: delay(),
                easing: 'ease-in-out',
                fill: 'forwards'
            });
    
            if (this.prefersReducedMotion) scaleAnimation.finish();
    
            this.previousAnimationElement = entry.target;
    
            await scaleAnimation.finished;
    
            this.previousAnimationElement = null;
    
            scaleAnimation.commitStyles();
            scaleAnimation.cancel();

            this.observer.unobserve(entry.target);
        } catch (error) {
            this.previousAnimationElement = null;
            document.documentElement.classList.add('js-no-waapi');
            this.observer.unobserve(entry.target);
            this.observer.disconnect();
        }
    }
}
